/**
 * Zustand Store — Central state management for the Studio Builder
 *
 * Replaces:
 *  - 18 useState hooks in StudioApp
 *  - HistoryManager (via zundo temporal middleware)
 *  - StorageManager (via zustand persist middleware)
 *  - StateUtils.debounce (handled by persist middleware)
 *
 * Modules that remain standalone:
 *  - Toast (DOM-based, accessed via Toast.success() etc.)
 *  - KeyboardShortcuts → useKeyboardShortcuts hook
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { temporal } from 'zundo';
import { defaultSettings, createDefaultComponents, defaultComponentOrder } from '@/data/defaults';
import { industryTemplates } from '@/data/templates';
import { reorder } from '@/utils/array';
import { migrateFromLegacyStorage, removeLegacyStorage } from './migrate-storage';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type BuilderMode = 'structure' | 'design' | 'export';
export type ViewMode = 'preview' | 'code';
export type Viewport = 'desktop' | 'tablet' | 'mobile';
export type PreviewPage = 'index' | 'angebot' | 'methoden' | 'ueberMich' | 'impressum' | 'datenschutz' | 'agb';
export type CodeTab = 'html' | 'css' | 'js' | 'angebot' | 'methoden' | 'ueberMich' | 'impressum' | 'datenschutz' | 'agb';
export type ModalName =
  | 'templates'
  | 'settings'
  | 'studio'
  | 'colorGen'
  | 'export'
  | 'offerteGen'
  | null;

// ---------------------------------------------------------------------------
// State Shape
// ---------------------------------------------------------------------------

interface BuilderState {
  // ---- Persisted data ----
  components: Record<string, any>;
  order: string[];
  settings: any;
  builderTheme: 'dark' | 'light';

  // ---- Transient UI state (not persisted) ----
  mode: BuilderMode;
  activeComponent: string | null;
  viewMode: ViewMode;
  viewport: Viewport;
  codeTab: CodeTab;
  previewPage: PreviewPage;
  openModal: ModalName;
  sidebarTab: string;
  sidebarCollapsed: boolean;
  showGrid: boolean;
  show8pxRaster: boolean;

  // ---- Actions ----
  // Component actions
  selectComponent: (id: string | null) => void;
  toggleComponent: (id: string) => void;
  updateComponent: (id: string, newData: any) => void;
  reorderComponents: (fromIndex: number, toIndex: number) => void;

  // Settings actions
  updateSettings: (newSettings: any) => void;

  // Template actions
  applyTemplate: (templateKey: string) => void;

  // Import
  importData: (data: { components?: any; order?: string[]; settings?: any }) => void;

  // UI actions
  setMode: (mode: BuilderMode) => void;
  setViewMode: (mode: ViewMode) => void;
  setViewport: (vp: Viewport) => void;
  setCodeTab: (tab: CodeTab) => void;
  setPreviewPage: (page: PreviewPage) => void;
  setOpenModal: (modal: ModalName) => void;
  setSidebarTab: (tab: string) => void;
  toggleSidebar: () => void;
  toggleGrid: () => void;
  toggle8pxRaster: () => void;
  toggleBuilderTheme: () => void;
  toggleDarkMode: () => void;
}

// ---------------------------------------------------------------------------
// Initial State
// ---------------------------------------------------------------------------

function getInitialState() {
  // Check for legacy localStorage data first
  const legacy = migrateFromLegacyStorage();

  const components = legacy?.components || createDefaultComponents();
  const order = legacy?.order || [...defaultComponentOrder];
  const builderTheme = legacy?.builderTheme || 'dark';

  // Deep merge settings: defaults ← saved values (preserves new fields)
  const savedSettings = legacy?.settings || {};
  const settings = {
    ...defaultSettings,
    ...savedSettings,
    companyLegal: {
      ...(defaultSettings.companyLegal || {}),
      ...(savedSettings.companyLegal || {}),
    },
    tracking: {
      ...(defaultSettings.tracking || {}),
      ...(savedSettings.tracking || {}),
    },
    pageAngebot: {
      ...(defaultSettings.pageAngebot || {}),
      ...(savedSettings.pageAngebot || {}),
      sections: {
        ...((defaultSettings.pageAngebot as any)?.sections || {}),
        ...((savedSettings.pageAngebot as any)?.sections || {}),
      },
    },
    pageMethoden: {
      ...(defaultSettings.pageMethoden || {}),
      ...(savedSettings.pageMethoden || {}),
      sections: {
        ...((defaultSettings.pageMethoden as any)?.sections || {}),
        ...((savedSettings.pageMethoden as any)?.sections || {}),
      },
    },
    pageUeberMich: {
      ...(defaultSettings.pageUeberMich || {}),
      ...(savedSettings.pageUeberMich || {}),
      sections: {
        ...((defaultSettings.pageUeberMich as any)?.sections || {}),
        ...((savedSettings.pageUeberMich as any)?.sections || {}),
      },
    },
  };

  // Clean up legacy keys if migration happened
  if (legacy) {
    removeLegacyStorage();
  }

  return { components, order, settings, builderTheme };
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

const initialState = getInitialState();

export const useBuilderStore = create<BuilderState>()(
  persist(
    temporal(
      (set) => ({
        // ---- Persisted data ----
        components: initialState.components,
        order: initialState.order,
        settings: initialState.settings,
        builderTheme: initialState.builderTheme as 'dark' | 'light',

        // ---- Transient UI state ----
        mode: 'structure' as BuilderMode,
        activeComponent: null,
        viewMode: 'preview' as ViewMode,
        viewport: 'desktop' as Viewport,
        codeTab: 'html' as CodeTab,
        previewPage: 'index' as PreviewPage,
        openModal: null as ModalName,
        sidebarTab: 'sections',
        sidebarCollapsed: false,
        showGrid: false,
        show8pxRaster: false,

        // ---- Component actions ----
        selectComponent: (id) =>
          set((state) => ({
            activeComponent: state.activeComponent === id ? null : id,
            // Auto-switch to Texte tab when selecting (not deselecting) in structure mode
            ...(state.activeComponent !== id && state.mode === 'structure'
              ? { sidebarTab: 'properties' }
              : {}),
          })),

        toggleComponent: (id) =>
          set((state) => ({
            components: {
              ...state.components,
              [id]: {
                ...state.components[id],
                enabled: !state.components[id]?.enabled,
              },
            },
          })),

        updateComponent: (id, newData) =>
          set((state) => ({
            components: {
              ...state.components,
              [id]: {
                ...state.components[id],
                data: {
                  ...state.components[id]?.data,
                  ...newData,
                },
              },
            },
          })),

        reorderComponents: (fromIndex, toIndex) =>
          set((state) => ({
            order: reorder(state.order, fromIndex, toIndex),
          })),

        // ---- Settings actions ----
        updateSettings: (newSettings) =>
          set(() => ({
            settings: newSettings,
          })),

        // ---- Template actions ----
        applyTemplate: (templateKey) =>
          set(() => {
            const template = industryTemplates[templateKey];
            if (!template) return {};

            // 1. Create fresh default components
            const newComponents = createDefaultComponents();

            // 2. Merge template component data
            Object.entries(template.components).forEach(
              ([type, data]: [string, any]) => {
                if (newComponents[type]) {
                  newComponents[type].data = {
                    ...newComponents[type].data,
                    ...data,
                  };
                  newComponents[type].enabled = true;
                }
              },
            );

            // 3. Build new settings
            const newSettings = {
              ...defaultSettings,
              ...template.settings,
              siteName:
                (template.components as any).header?.companyName ||
                template.name,
              companyLegal: {
                ...(defaultSettings.companyLegal || {}),
                name:
                  (template.components as any).footer?.companyName ||
                  (defaultSettings as any).companyLegal?.name,
                address:
                  (template.components as any).footer?.address ||
                  (defaultSettings as any).companyLegal?.address,
                email:
                  (template.components as any).footer?.email ||
                  (defaultSettings as any).companyLegal?.email,
                phone:
                  (template.components as any).footer?.phone ||
                  (defaultSettings as any).companyLegal?.phone,
              },
              pageAngebot: {
                ...(defaultSettings.pageAngebot || {}),
                ...(template.settings?.pageAngebot || {}),
                sections: {
                  ...((defaultSettings.pageAngebot as any)?.sections || {}),
                  ...((template.settings?.pageAngebot as any)?.sections || {}),
                },
              },
              pageMethoden: {
                ...(defaultSettings.pageMethoden || {}),
                ...(template.settings?.pageMethoden || {}),
                sections: {
                  ...((defaultSettings.pageMethoden as any)?.sections || {}),
                  ...((template.settings?.pageMethoden as any)?.sections || {}),
                },
              },
              pageUeberMich: {
                ...(defaultSettings.pageUeberMich || {}),
                ...(template.settings?.pageUeberMich || {}),
                sections: {
                  ...((defaultSettings.pageUeberMich as any)?.sections || {}),
                  ...((template.settings?.pageUeberMich as any)?.sections || {}),
                },
              },
            };

            return {
              components: newComponents,
              settings: newSettings,
              activeComponent: null,
            };
          }),

        // ---- Import ----
        importData: (data) =>
          set(() => ({
            ...(data.components ? { components: data.components } : {}),
            ...(data.order ? { order: data.order } : {}),
            ...(data.settings ? { settings: data.settings } : {}),
          })),

        // ---- UI actions ----
        setMode: (mode) => set({
          mode,
          sidebarTab: mode === 'structure' ? 'sections'
            : mode === 'design' ? 'brand'
            : 'settings',
        }),
        setViewMode: (mode) => set({ viewMode: mode }),
        setViewport: (vp) => set({ viewport: vp }),
        setCodeTab: (tab) => set({ codeTab: tab }),
        setPreviewPage: (page) => set({ previewPage: page }),
        setOpenModal: (modal) => set({ openModal: modal }),
        setSidebarTab: (tab) => set({ sidebarTab: tab }),
        toggleSidebar: () => set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),
        toggleGrid: () => set((state) => ({ showGrid: !state.showGrid })),
        toggle8pxRaster: () => set((state) => ({ show8pxRaster: !state.show8pxRaster })),

        toggleBuilderTheme: () =>
          set((state) => ({
            builderTheme: state.builderTheme === 'dark' ? 'light' : 'dark',
          })),

        toggleDarkMode: () =>
          set((state) => ({
            settings: {
              ...state.settings,
              darkMode: !state.settings.darkMode,
            },
          })),
      }),
      {
        // zundo options: only track data changes for undo/redo
        partialize: (state) => ({
          components: state.components,
          order: state.order,
          settings: state.settings,
        }),
        limit: 30,
      },
    ),
    {
      // zustand persist options
      name: 'smooth_builder',
      partialize: (state) => ({
        components: state.components,
        order: state.order,
        settings: state.settings,
        builderTheme: state.builderTheme,
      }),
    },
  ),
);

// ---------------------------------------------------------------------------
// Temporal store accessor (for undo/redo in hooks/components)
// ---------------------------------------------------------------------------

import { useStore } from 'zustand';

type TemporalStoreState = ReturnType<typeof useBuilderStore.temporal.getState>;

export function useTemporalStore<T>(selector: (state: TemporalStoreState) => T): T {
  return useStore(useBuilderStore.temporal, selector);
}
